import { useState } from "react";
import { CheckCircle2, XCircle, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePortal } from "@/pages/admin/PortalLayout";

export default function PortalSecurity() {
  const [run, , setRun] = usePortal("/portal/security-tests/latest");
  const [state] = usePortal("/admin/auth/state");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/portal/security-tests/run"); setRun(data); toast.success(`${data.passed}/${data.total} passed`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="portal-security">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-2xl font-bold md:text-3xl">Security tests A–J</h1><p className="text-sm text-muted-foreground">Runs live server-side checks of the PLATFORM_SUPER_ADMIN → MFA → EMAIL OTP → PORTAL_USER_MANAGER → PORTAL chain (test OTP emails are suppressed).</p></div>
        <button onClick={go} disabled={busy} data-testid="security-run-btn" className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Run tests
        </button>
      </div>
      {state && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="security-session-states">
          {Object.entries(state.states).map(([k, v]) => (
            <div key={k} className="rounded-xl border bg-card p-3 text-xs shadow-sm">
              <p className="font-mono text-[10px] text-muted-foreground">{k}</p>
              <p className={`mt-1 flex items-center gap-1 font-bold ${v ? "text-[#0F5132] dark:text-emerald-400" : "text-[#842029]"}`}>{v ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {v ? "TRUE" : "FALSE"}</p>
            </div>
          ))}
        </div>
      )}
      {run && run.total > 0 && <p className="text-sm font-semibold" data-testid="security-summary">{run.passed}/{run.total} passed · {new Date(run.at).toLocaleString()}</p>}
      <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
        <table className="w-full text-sm" data-testid="security-results-table">
          <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-3">Test</th><th className="p-3">Scenario</th><th className="p-3">Result</th><th className="p-3">Evidence</th></tr></thead>
          <tbody>
            {(run?.results || []).map((r) => (
              <tr key={r.id} className="border-t" data-testid={`security-test-${r.id}`}>
                <td className="p-3 font-bold">{r.id}</td><td className="p-3">{r.name}</td>
                <td className="p-3 text-xs font-bold">{r.passed ? <span className="inline-flex items-center gap-1 text-[#0F5132] dark:text-emerald-400"><CheckCircle2 size={14} /> PASS</span> : <span className="inline-flex items-center gap-1 text-[#842029]"><XCircle size={14} /> FAIL</span>}</td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground">{r.detail}</td>
              </tr>
            ))}
            {!run?.results?.length && <tr><td colSpan={4} className="p-6 text-center text-sm text-muted-foreground">No run yet – press “Run tests”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
